import { createContext, useContext, useEffect, useState, useRef, ReactNode, useMemo } from "react";

import { useAuth } from "./AuthContext";

interface WebshopFilterContextType {
  selectedWebshopId: number | null;
  setSelectedWebshopId: (id: number | null) => void;
  availableWebshops: Array<{ id: number; name: string }>;
  loading: boolean;
}

const WebshopFilterContext = createContext<WebshopFilterContextType | undefined>(undefined);

export function WebshopFilterProvider({ children }: { children: ReactNode }) {
  const { accessibleWebshops, isAdmin } = useAuth();
  const [selectedWebshopId, setSelectedWebshopId] = useState<number | null>(null);
  const [availableWebshops, setAvailableWebshops] = useState<Array<{ id: number; name: string }>>([]);
  const [loading, setLoading] = useState(true);
  const hasInitialized = useRef(false);

  useEffect(() => {
    const fetchWebshops = async () => {
      // Wait for accessibleWebshops to be loaded
      if (accessibleWebshops === null || accessibleWebshops === undefined) {
        return;
      }

      // If user has no accessible webshops, set empty and stop loading
      if (accessibleWebshops.length === 0) {
        setAvailableWebshops([]);
        setLoading(false);
        return;
      }

      try {
        const { supabase } = await import("@/integrations/supabase/client");
        
        // Filter out any null or invalid values
        const validWebshopIds = accessibleWebshops.filter(id => id !== null && id !== undefined && !isNaN(id));
        
        if (validWebshopIds.length === 0) {
          setAvailableWebshops([]);
          setLoading(false);
          return;
        }
        
        let query = supabase
          .from("webshops")
          .select("id, name")
          .order("name");

        // Filter by accessible webshops unless admin
        if (!isAdmin) {
          query = query.in('id', validWebshopIds);
        }

        const { data, error } = await query;

        if (error) throw error;
        setAvailableWebshops(data || []);

        // Auto-select first webshop ONLY on initial load, not when user selects "Alle butikker"
        if (!hasInitialized.current && data && data.length > 0) {
          setSelectedWebshopId(data[0].id);
          hasInitialized.current = true;
        }
      } catch (error) {
        console.error("Error fetching webshops:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchWebshops();
  }, [accessibleWebshops, isAdmin]);

  const contextValue = useMemo(() => ({
    selectedWebshopId,
    setSelectedWebshopId,
    availableWebshops,
    loading,
  }), [selectedWebshopId, setSelectedWebshopId, availableWebshops, loading]);

  return (
    <WebshopFilterContext.Provider value={contextValue}>
      {children}
    </WebshopFilterContext.Provider>
  );
}

export function useWebshopFilter() {
  const context = useContext(WebshopFilterContext);
  if (context === undefined) {
    throw new Error("useWebshopFilter must be used within a WebshopFilterProvider");
  }
  return context;
}
